/**
 * @description - Add all event names here, so we have consistency around the application and clarity on what events exists.
 * If the title field is dynamic then add it as a function.
 * @example
 * ```ts
 * const MIXPANEL_EVENT_NAMES = {
 *  STATIC_VALUE: "SOME_STATIC_TITLE",
 *  DYNAMIC_VALUE: (dynamicString) => `SOME_TEXT - ${dynamicString}`
 * }
 * ```
 */
const MIXPANEL_EVENT_NAMES = {
  VISITED: 'Visited',
  DOMAIN_AUTOCORRECTED: 'Domain autocorrected',
  DOMAIN_DID_YOU_MEAN_CLICKED: 'Domain Did you mean clicked',

  HOMEPAGE: {
    COLUMN_SORT_CLICKED: 'Column Sort Clicked - Homepage Table',
    VIEW_MORE_CLICKED: 'View More Clicked - Homepage Table',
    PRODUCT_ROW_CLICKED: 'Product Trading View Initiated - Trade',
    PRODUCT_TAB_CLICKED: 'Product Tab Clicked - Homepage Table',
    FILTERING_TAB_CLICKED: 'Filtering Tab Clicked - Homepage Table',
    FUTURE_BANNER_CLICKED: 'Future Banner Clicked - Homepage',
  },
  OPTIONS_ANALYTICS: 'Options Analytics',
  OPTIONS_LEADERBOARD: 'Options LeaderBoard',
  BRACKET_ORDER: {
    TRADE_TABLE_NEW: 'Bracket Set - Trade - Table',
    TRADE_TABLE_EDIT: 'Bracket Edited - Trade - Table',
    ADD_BRACKET_CTA_CLICKED: 'Add Bracket CTA clicked',
    TRIGGER_INDEX_TOOLTIP_CLICKED: 'Trigger Index tooltip clicked',
    TRIGGER_INDEX_PRICE_CLICKED: 'Trigger Index price dropdown clicked',
    TRIGGER_INDEX_PRICE_SELECTED: 'Trigger Index price selected',
    PRICE_ENTERED: 'Price entered bracket order',
    RECOMMENDED_PRICE_EDITED: 'Bracket Order - Recommended Price Edited',
    RECOMMENDED_PRICE_CLICKED: 'Bracket Order - Recommended Price clicked',
    CHARTS_BRACKET_SET: 'Charts Bracket Set',
    CHARTS_BRACKET_UPDATED: 'Charts Bracket Updated',
    CHARTS_BRACKET_REMOVED: 'Charts Bracket Removed',
    BREAK_EVEN_TRIGGER: 'Bracket Order - Breakeven warning triggered',
  },
  ACCOUNT_EQUITY: 'Account Equity',
  ACCOUNT: {
    IDENTITY_VERIFICATION: {
      ALL_PAGES: 'Identity Verification Clicked - All Pages',
    },
    API_KEYS: {
      API_KEY_CREATED: 'API Key Created',
    },
    ACCOUNT_ANALYTICS: 'Account Analytics',
    TRANSACTION_LOGS: {
      ANNUAL_ACCOUNT_SUMMARY: {
        EMAIL_SUMMARY_REPORT: 'Email Account Summary Report Clicked',
      },
    },
    ACCOUNT_ANALYTICS_REFERRAL_BANNER: 'Refer and Earn (P&L analytics)',
  },
  IMPORT_KYC: {
    DO_FRESH_KYC: 'I’ll do a fresh KYC clicked',
    IMPORT_KYC_INITIATED: 'Import KYC initiated',
    SEND_OTP_CLICKED: 'Send OTP clicked',
    RESEND_OTP_CLICKED: 'Resend OTP clicked',
    IMPORT_KYC_FAILED: 'KYC Import Failed',
    MOBILE_OTP_VERIFICATION_FAILED: 'Mobile OTP Verification Failed',
    RESEND_MOBILE_NUMBER_OTP_CLICKED: ' Resend mobile number OTP clicked',
  },
  SIGN_IN: {
    SUCCESS: 'Sign In Success',
    FAIL: 'Sign In Failed',
    COUNTRY_CHANGE: 'Country Selection - Signup Page',
    INVALID_OAUTH_PROVIDER_SELECTED: 'Invalid oauth provider selected',
    GOOGLE_ACCESS_TOKEN_VALIDATION_FAILED: 'Google Access Token validation failed',
  },
  LOGIN: {
    BUTTON_CLICKED: 'Login button clicked',
    BUTTON_CLICKED_SUCCESSFULLY: 'Login button clicked successfully',
    DELTA_INDIA_LOGIN_LINK_CLICKED_ON_GLOBAL: 'Delta India Log In Clicked',
  },
  SIGN_UP: {
    SNIPER_LINK_CLICKED: 'Sniper link clicked',
    BUTTON_CLICKED: 'Signup button clicked',
    EMAIL_VERIFICATION_COMPLETED: 'Email Verification Completed',
    EMAIL_OTP_VERIFICATION_FAILED: 'Email OTP Verification Failed',
    SUCCESS: 'Register Success',
    FAIL: 'Register Failed',
    BUTTON_CLICKED_WITH_MISSING_FIELDS: 'Signup button clicked - Missing fields',
    API_CATCH_BLOCK: 'Register Failed - api catch block',
    DEFAULT_CATCH_BLOCK: 'Register Failed - default catch block',
    COUNTRY_SELECTION: 'Country Selection - Signup Page',
    RESEND_EMAIL_CODE: 'Resend Email Verification Code',
    INVALID_OAUTH_PROVIDER_SELECTED: 'Invalid oauth provider selected',
    GOOGLE_ACCESS_TOKEN_VALIDATION_FAILED: 'Google Access Token validation failed',
    DELTA_INDIA_TRANSFER_LINK_CLICKED: 'Delta India transfer link clicked',
    SIGN_UP_DELTA_INDIA_CLICKED: 'Sign up on Delta India clicked',
    GOOGLE_ONE_TAP_SHOWN: 'Google One Tap Shown',
    GOOGLE_ONE_TAP_FAILED: 'Google one tap failed',
    GOOGLE_ONE_TAP_CLOSED: 'Google One Tap Closed',
  },
  REFERRAL_CODE: {
    VALID: 'User Input - Valid Referral Code',
    INVALID: 'User Input - Invalid Referral Code',
  },
  LINK_ACCOUNT: {
    FAIL: 'Account linking failed',
    SUCCESS: 'Account linking successful',
    INITIATED: 'Account linking initiated',
  },
  CONSENT: {
    SIGNUP_BUTTON_CLICKED_CONSENT_MISSING: 'Signup button Clicked - Consent Missing',
    OAUTH_DONE_CONSENT_PENDING: 'Oauth Done Consent Pending',
    CHECKBOX_SELECTED: 'Checkbox Selected',
    CHECKBOX_UNSELECTED: 'Checkbox Unselected',
    POP_UP_CLOSED: 'Pop-up closed',
  },
  DEPOSITS: {
    RECOMMENDED_DEPOSIT: {
      LEARN_MORE: 'Deposit Prompt - Learn More Click',
    },
    DEPOSIT_CLICKED_HEADER: 'Deposit - Header',
    DEPOSIT_CLICKED_ALL_PAGES: 'Deposit Clicked - All Pages',
    BUY_CRYPTO_CLICKED: 'Buy Crypto Clicked',
    ADD_FUND_CLICKED_HEADER: 'Add funds clicked - Header',
    INITIATE_ADD_FUNDS_CLICKED: 'Initiate add funds clicked',
    DEPOSIT_ACCOUNT_DROPDOWN_CLICKED: 'Deposit Account dropdown clicked',
    DEPOSIT_DROPDOWN_METHOD_CLICKED: 'Deposit method dropdown clicked',
    DEPOSIT_AMOUNT_BUTTONS_CLICKED: 'Deposit amount buttons clicked',
    ADD_FUND_CLICKED: 'Add funds clicked',
    BUY_CRYPTO_INITIATED: 'Crypto Buy Initiated',
    CONTINUE_TO_PAY_CLICKED: 'Continue to Pay clicked',
    NET_BANKING_LINK_CLICKED: 'Netbanking link clicked',
    READ_FAQ_CLICKED: 'Read FAQ clicked',
    QUESTION_MARK_CLICKED: 'Question mark clicked',
    ADD_UPI_ID: 'Add UPI ID',
    UPI_ID_STATUS: 'UPI ID status',
    ADD_FUNDS_PAYMENT_METHOD_TAB_CLICKED: 'Add Funds_Payment method tab clicked',
    HELP_BUTTON_CLICKED: 'Help button clicked',
    UTR_SUBMITTED: 'UTR Submitted',
    DEPOSIT_PROOF_SUBMITTED: 'Deposit Proof Submitted',
    AMOUNT_ENTERED_MANUALLY: 'Amount entered manually',
  },
  TRANSFER_AMOUNT: {
    ACCOUNT_NUMBER_COPIED: 'Account number copied',
    IFSC_CODE_COPIED: 'IFSC code copied',
    PAID_BUTTON_CLICKED: 'I have paid clicked',
    TRANSFER_ACCOUNT_SWITCHED: 'Bank transfer_account switched',
  },
  LOGIN_EMAIL_VERIFICATION: {
    FAIL: 'Login Email OTP failed',
    MOBILE_OTP_VERIFIED: 'Mobile OTP Verified',
    MOBILE_OTP_REQUESTED: 'Mobile OTP Requested',
    '2FA_AUTHENTICATION': {
      FAIL: '2FA Authentication Failed',
    },
    RESEND_EMAIL_CODE: 'Resend Email Verification Code',
  },
  OPTIONS_CHAIN: {
    CONTRACT_CHANGE: 'Contract Changed - Trade - Option Chain',
    COLUMNS_CHANGE: 'Columns changed - Trade Page - Option Table View',
    STRIKE_PRICE_SELECTED: 'Strike Price Selected',
    FAVORITE_MARKED: 'Favorite Marked - Trade',
    FAVORITE_UNMARKED: 'Favorite UnMarked - Trade',
    EXPIRTY_DATE_CHANGED: 'Expiry Date Changed - Trade - Option Chain',
    VIEW_SELECTED: 'Option Chain View Selected',
    UNDERLYING_PRICE_BUTTON_CLICKED: 'Underlying price button clicked',
    T_SHAPE_FILTER_CLICKED: 'T-Shape option chain filtering tab clicked',
  },
  ADD_BANK_ACCOUNT: {
    ADD_BANK_DETAILS: 'Bank Details clicked',
    ADD_BANK_FAILED: 'Add Bank Failed',
    ADD_BANK_NAME_MISMATCH: 'Add Bank name mismatch',
    ADD_BANK_OTP_VERIFICATION_COMPLETED: 'Add Bank OTP Verification Completed',
    ADD_DELETE_BANK_ACCOUNT: 'Add/Delete - Bank Account',
    ADD_ACCOUNT: 'Add Bank - Initiated',
    VERIFY: 'Add Bank - Verification',
    RESEND_OTP: 'Bank - OTP resend',
  },
  DELETE_BANK_ACCOUNT: {
    INITIATED: 'Delete Bank Initiated',
    DELETE_BANK_CONFIRMED: 'Delete Bank Confirmed',
    DELETE_STATUS: 'Delete Bank Status',
    DELETE_BANK_ACCOUNT: 'Delete bank account clicked',
    DELETE_BANK_ACCOUNT_CONFIRMATION: 'Delete bank account confirmed clicked',
  },
  WITHDRAWAL: {
    WITHDRAWAL_CLICKED_ALL_PAGES: 'Withdrawal Clicked - All Pages',
    WITHDRAWAL_CLICKED_SUPPORT_TICKET: 'Withdrawal Clicked - Support Ticket',
  },
  WITHDRAWAL_BANK: {
    TO_BANK_ACCOUNT: 'Withdraw to Bank Account',
    INITIATED: 'Bank Withdraw Initiated',
    VERIFY: 'Bank Withdraw - Verification',
  },
  BASKET_ORDER: {
    PLACE_ORDER: 'Basket Order - Order Placed',
    BUY_SELL_CLICKED: 'Basket Order - Mobile Basket Orders - Buy/Sell Button Clicked',
  },
  POSITION_ANALYSE: {
    POSITION_ANALYZE_CLICKED: 'Position Analyze Clicked',
    CLOSE_POSITIONS_CLICKED_PAY_OFF_CHART: 'Close Positions Clicked - Payoff Chart',
    GROUP_BY_EXPIRY_TOGGLE_CLICKED: 'Group By Expiry Toggle Clicked',
    PAYOFF_CHART_SELECTOR_SWITCHED: 'Payoff Chart Selector Switched',
  },
  ORDER_PLACEMENT: {
    LEVERAGE_SLIDER_MOVED: 'Leverage Slider Moved - Trade - Ordering',
    PLACE_ORDER: 'Order Placed - Trade - Ordering',
    BUY_CLICK: 'Buy switch clicked - Trade - Ordering',
    SELL_CLICK: 'Sell switch clicked - Trade - Ordering',
    LEVERAGE_BUTTON_CLICK: ' Leverage button clicked - Trade - Ordering',
    MARGIN_BUTTON_CLICK: 'Margin mode button clicked - Trade - Ordering',
    LEVERAGE_REJECT: ' Leverage Change Rejected - Trade - Ordering',
    MARGIN_REJECT: 'Margin Change Rejected - Trade - Ordering',
    PRICE_ENTERED: 'Price entered - Trade - Ordering',
    QUANTITY_ENTERED: 'Quantity entered - Trade - Ordering',
  },
  CARDS_AREA: {
    BANNER_CLICK: 'Offer Card - Markets Page',
    OFFER_CARD_CLICK: 'Offer Card Clicked',
  },
  MARKETS: {
    SUCCESS_SIGNUP: 'Country Confirmed - Identity Verification',
    OPTION_COMBOS_TAB_CLICKED: ' Tab clicked - markets - Product table_option combos',
    TRADE_OPTION_COMBOS: 'Product Trading View initiated - trade_option combos',
    TRADE_OPTION: 'Product Trading View Initiated - Trade',
    PRODUCT_TABLE_CLICK: 'Tab Clicked - Product Dropdown - Product Table',
    COMBOS_FILTER_CLICK: 'All Markets Page - combos filter clicked',
    MARKETS_OVERVIEW_CONTRACT_CLICKED: 'Markets Overview - contract clicked',
    MARKETS_OVERVIEW_OFFER_TOGGLE_CLICKED: 'Markets Overview - offer toggle clicked',
  },
  IDENTITY_VERIFICATION: {
    GET_OTP_CLICKED: 'Get OTP Clicked - Identity Verification',
    FETCH_VENDOR_CONFIG_FAILED: 'Fetch vendor config failed',
  },
  STRIP_CLICKED: 'Strip Clicked',
  PRICE_CHANGE_DISTRIBUTION_TOOLTIP: 'advancer-decliner-tooltip',
  POPUP_CARD_CLICKED: 'Popup Card Clicked',
  INITIATE_KYC_CLICKED: 'Initiate KYC Clicked',
  DELTA_DND: {
    COLUMNS_RESELECTED: 'Columns Re-selected',
  },
  PAYMENT_PARTNER: {
    Payment_Partner_Dropdown_Clicked: 'Payment partner_dropdown clicked',
    Payment_Partner_Selected: 'Payment partner_selected',
  },
  HYPERVERGE_KYC_ERROR: 'Hyperverge KYC error',
  HYPVERGE_USER_CANCELLED: 'Hyperverge KYC cancelled',
  HYPERVERGE_SDK_LOAD_FAIL: 'Hyperverge SDK Load Fail',
  PNL_SHARE_CARD_CLICKED: `PNL Share Card Clicked`,
  PNL_SHARE_CARD_APP: 'PNL Share Card App',
  RAISE_SUPPORT_TICKET_CLICKED: 'Raise Support ticket link clicked',
  OPTIONS_SPREAD_STRIP_CLICK: 'Options Spread strip clicked',
  SET_OR_CHANGE_PASSWORD: 'Set/Change password',
  IN_APP_REVIEW: 'Rating popup shown',
  POSITION_VIEW_CHANGED: 'Position View Changed',
  CALENDLY_BANNER_CLICKED: 'Calendly_link_clicked',
  API_KEY_PAGE: {
    ROOT_EVENT: 'API Keys Page',
    IP_ADDRESS_SELECTED: 'IPAddressSelected',
    KEY_CREATED: 'API Key Created',
    CREATE_API_CLICKED: 'Create API Clicked',
    OTP_SUBMITTED: 'OTP Submitted',
    WHITELIST_EDITED: 'Whitelist Edited',
    INVALID_IP: 'Invalid IP entered',
    INVALID_API_KEY_NAME: 'Invalid API Key Name entered',
    WHITELIST_EDIT_CLICKED: 'Whitelist Edit clicked',
    DOWNLOAD_REST_CLIENT_CLICKED: 'download_rest_client_clicked',
    API_DOCS_CLICKED: 'api_documentation_clicked',
    CALENDLY_LINK_BUTTON_CLICKED: 'Calendly Link Button Clicked',
    COPILOT: 'Build with Delta AI Clicked',
  },
  DELTA_INDIA_MARKETING_BANNER: `Delta India Banner Clicked`,
  SIGNAL_TRADING: {
    ROOT_EVENT: 'Signal Trading',
    CREATE_WEBHOOK_CLICKED: 'Create Webhook Clicked',
    OTP_GEN_SUCCESS: 'OTP Generated Successfully',
    WEBHOOK_CREATED: 'Webhook Created',
    VIEW_TUTORIAL: 'View Tutorial Clicked',
    WEBHOOK_DETAILS_REFETCHED: 'Webhook Details Refetched',
  },
  ADD_FUNDS_VIDEO_TUTORIAL: 'Video Tutorial Opened - Add Funds',
  DELTA_INDIA_TRANSFER_LINKED_CLICKED: 'Delta India Transfer Linked Clicked',
  NETWORK_DATA_REQUEST: 'Network Data Request',
  MANDATORY_DECLARATION_POPUP_UPDATE_FAILED: 'Mandatory declaration popup update failed',
  MANDATORY_DECLARATION_POPUP_STATE_SUBMITTED: 'Mandatory declaration popup submitted',
  MANDATORY_DECLARATION_POPUP_CLOSED: 'Mandatory declaration popup closed',
  MANDATORY_DECLARATION_POPUP_STATE_SELECTED:
    'Mandatory declaration popup_state selected',
  MANDATORY_DECLARATION_POPUP_OCCUPATION_SELECTED:
    'Mandatory declaration popup_occupation selected',
  MANDATORY_DECLARATION_POPUP_INCOME_SELECTED:
    'Mandatory declaration popup_income selected',
  MANDATORY_DECLARATION_POPUP_UPDATED: 'Mandatory declaration updated',
  SHARE_CARD_TRADE_TABLE: 'Position Card Share Button Clicked - Trade - Table',
  POSITION_CARD_SHARE_BUTTON: 'Position Card Share Button Clicked',
  PNL_SHARE_CARD_POSITION: 'PNL Share Card App Position',
  MOBILE_CHART: {
    SETTINGS_BUTTON_CLICKED: 'Charts/Book_Setting icon clicked',
    BOOK_ORDER_BUTTON_HIDDEN: 'Charts/Book_Order buy/sell button hidden',
    BOOK_ORDER_BUTTON_SHOWN: 'Charts/Book_Order buy/sell button shown',
    BOOK_ORDER_MODAL_DISABLED: 'Charts/Book_Order confirmation disabled',
    BOOK_ORDER_MODAL_ENABLED: 'Charts/Book_Order confirmation enabled',
  },
  CHART_TYPE: {
    DROPDOWN_CLICKED: 'Chart Type Dropdown Clicked',
    CLICKED: 'Chart Type Clicked',
    TAB_CLICKED: 'CH OB RT Tab Clicked',
  },
  REACTIVATE_ACCOUNT: {
    REACTIVATE_ACCOUNT_CLICKED: 'Reactivate Account Clicked',
    REACTIVATE_ACCOUNT_STATUS: 'Reactivate Account Status',
  },
  DELTA_GLOBAL_LINK_CLICKED: 'Delta Global Redirection Link Clicked',
  INDIA_OR_GLOBAL_MODAL: {
    SUBMIT: 'Domain change_popup_CTA clicked',
    CHANGE_PREFERENCE: 'Domain change_preference selected',
    REMEMBER_MY_CHOICE: 'Domain change_remember my choice',
  },
  TRADE_SUCCESS_SHARE_CARD: {
    CARD_CTA_CLICKED: 'Trade Success Share Clicked',
    SHARE_VIA_CTA_CLICKED: 'Trade Success Shared',
  },
  CLAIM_UTR_TRY_AGAIN_CLICKED: 'Claim UTR Try Again Clicked',
  CLAIM_UTR_RAISE_SUPPORT_TICKET_CLICKED: 'Claim UTR Raise Support Ticket Clicked',
  STRATEGY_PNL_BUTTON_CLICKED: 'Strategy_PnL_button_clicked',
  SCALPER_OFFER: {
    SCALPER_BUTTON_CLICKED: 'Scalper Button Clicked',
    SCALPER_ENROLLED: 'Scalper Enrolled',
    SCALPER_BLOG_LINK_CLICKED: 'Scalper Blog Link Clicked',
    SCALPER_TRADE: 'Scalping Trade',
  },
  XMAS_OFFER: {
    XMAS_BUTTON_CLICKED: 'X-Mas offer button clicked',
  },
  GLOBAL_USER_SIGN_UP_CLICKED: 'Global User Signup Clicked',
  GLOBAL_USER_CLICK_HERE_CLICKED: 'Global User Click Here Clicked',
  TESTNET_LINK_CLICKED: 'Testnet link clicked',
  BASKET_ORDER_SELECTED: 'Basket Order - Selected',
  BASKET_ORDER_MORE_HEADER_SELECTED: 'Basket order more header selected',
  REDUCE_MARGIN_TOOLTIP_CLICKED: 'Reduce Margin Tooltip clicked',
  PREFERENCES_CLICKED: 'Preferences clicked',
  BASKET_ORDER_ADD_CONTRACT_CTA_CLICKED: 'Basket Order - Add Contract CTA clicked ',
  BASKET_ORDER_LOGOUT_MODAL_OPENED: 'Basket order pop up CTA clicked',
  DISABLE_2FA_WITH_FACE_AUTH: {
    EMAIL_VERIFIICATION_SUCCESS: 'Lost 2FA Email Verification Success',
    FACE_AUTH_INITIATED: 'Lost 2FA Face Auth Initiated',
    LOST_2FA_FACE_AUTH_STATUS: 'Lost 2fa Face Auth Status',
  },
  DOWNLOAD_APP_POPUP: 'App download sticky popup',
  CAPTCHA_ERROR: 'ReCaptcha Failed',
  ACKNOWLEDGE_FIRST_TRADE_POPUP: 'Acknowledge CTA clicked',
  CLICK_HERE: {
    SIGN_UP_CLICKED: 'Click Here - Sign Up Clicked',
  },
  API_FAILURE_TRACKING: 'API failure tracking',
  API_TIMEOUT_TRACKING: 'API timeout tracking',
  USER_PROFILE: {
    EDIT: 'Profile Edit Clicked',
    FACEAUTH_INITIATED: 'Profile Edit FaceAuth Initiated',
    SUCCESS: 'Profile Edit Success',
    ERROR: 'Profile Edit Failed',
  },
  REKYC: {
    CLICKED: 'Re-Kyc Clicked',
    REKYC_FACE_AUTH_FAIL: 'ReKYC FaceAuth Fail',
    REKYC_FACE_AUTH_SUCCESS: 'ReKYC FaceAuth Success',
    REKYC_FACE_AUTH_INITIATED: 'ReKYC FaceAuth Initiated',
  },
  UPDATE_ADDRESS: {
    INITIATED: 'Address Update Initiated',
    STATUS: 'Address Update Status',
  },
  CUSTOM_ALERTS: {
    CREATED: 'Custom Alert Created',
    DELETED: 'Custom Alert Deleted',
    PRICE_CHANGE_BUTTON_USED: 'Custom Price Buttons Used',
    SWITCH_BUTTON_USED: 'Alert switch clicked',
    MANAGE_ALL_ALERTS_CLICKED: 'Manage All Alerts Clicked',
    ADD_NEW_ALERT_CLICKED: 'Add New Alert Clicked',
    ALERTS_TOP_COIN_CLICKED: 'Alerts top coin click',
    OPEN_APP_SETTINGS_INTERACTED: 'Open App Settings Interacted',
  },
  ORDERBOOK: {
    SHOW_DEFAULT_VIEW_CLICKED: 'Show Default View Order Book Clicked',
    LOAD_MORE_ORDERBOOK_CLICKED: 'Load More Order Book Clicked',
  },
  GROWTHBOOK_INIT: 'Growthbook load feature initialization',
  GROWTHBOOK_EXPERIMENT_STARTED: 'Growthbook Experiment Started',
  GROWTHBOOK_FEATURE_USED: 'Growthbook Feature Used',
  AUTO_TOPUP_TOOLTIP_CLICKED: 'Auto top-up tooltip clicked',
  LIVE_CHAT_CLICKED: 'Chatbot module clicked',
  MOVE_ENTRY_POINT_CLICKED: 'MOVE Entrypoint Clicked',
  MOVE_TRADING_VIEW_INITIATED: 'MOVE Trading view Initiated',
  STRADDLE_INTRO_CLICKED: 'What Are Straddles Clicked',
  PAY_OFF_CTA_CLICKED: 'Payoff CTA Clicked ',
  SHARE_CARD: {
    OPENED: 'Share Card Opened',
    SHARED: 'Share Card Shared',
    SHARE_FAILED: 'Share Card Share Failed',
  },
  FORGOT_EMAIL_ID: {
    INITIATED: 'Forgot Email Initiated',
    OTP_REQUESTED: 'Forgot Email OTP Requested',
    OTP_SENT: 'Forgot Email OTP Sent',
    OTP_FAILED: 'Forgot Email OTP Failed',
    OTP_VERIFIED: 'Forgot Email OTP Verified',
  },
  QUICKORDER: {
    CHART_CONTEX_MENU_BUY_SELL_CLICK: 'Charts Plus Button - Buy/Sell Clicked',
    CHART_CONTEX_MENU_CREATE_ORDER_CLICK: 'Charts Plus Button - Add Order Clicked',
  },
  SPOT_TRADING: {
    YOU_RECEIVE_TOOLTIP_CLICK: 'You Receive Tooltip Click',
    TRANSFER_FUNDS_CLICKED: 'Spot Transfer Funds Clicked',
    ADD_FUNDS_CLICK_TRANSFER_FUNDS: 'Add Funds Click - Transfer Funds',
    WALLET_TRADE_CTA_CLICKED: 'Spot Wallet Trade CTA Clicked',
    WALLET_COIN_TRADE_CLICKED: 'Spot Wallet Coin Trade Clicked',
    TDS_SUMMARY_DOWNLOAD_CLICKED: 'TDS Summary Download Clicked',
    TDS_SUMMARY_EMAIL_CLICKED: 'TDS Summary Email Clicked',
    ENTRYPOINT_CLICKED: 'Spot Entrypoint Clicked',
    SPOT_FNO_AMOUNT_TRANSFER: 'Spot FNO Amount Transferred',
  },
  MOBILE_TRADE_HOLDINGS: {
    ORDER_CANCELLED: 'Order Cancelled - Trade Page',
    POSITION_CLOSED: 'Position Closed - Trade Page',
  },
  CLOSE_POSITION: 'Position Closed - Trade - Table',
  TRADFI_SELECTED: 'TradeFi Entrypoint Clicked',
} as const;

export const ALGO_MARKETPLACE_EVENTS = {
  COMMON: {
    COPY_NOW_CLICKED: 'copy_now_clicked',
    ADD_FUNDS_CLICKED: 'add_funds_clicked',
    EXIT_STRATEGY_CLICKED: 'exit_strategy_clicked',
    FORCE_EXIT_CLICKED: 'force_exit_clicked',
    CANCEL_EXIT_CLICKED: 'cancel_exit_clicked',
    EDIT_STOP_LOSS_CLICKED: 'edit_stop_loss_clicked',
    VIEW_DASHBOARD_CLICKED: 'view_dashboard_clicked',
    LOGIN_TO_COPY_CLICKED: 'login_to_copy_clicked',
  },
  HOME: {
    MARKETPLACE_VIEWED: 'marketplace_viewed',
    HOW_IT_WORKS_CLICKED: 'how_it_works_clicked',
    COPY_TRADING_DISCLAIMER_VIEWED: 'copy_trading_disclaimer_viewed',
  },
  STRATEGY_DETAILS: {
    STRATEGY_DETAILS_VIEWED: 'strategy_details_viewed',
    STRATEGY_DETAILS_URL_CLICKED: 'strategy_details_url_clicked',
    METRICS_CLICKED: 'metric_tooltip_clicked',
  },
  EDIT_STOP_LOSS: {
    STOP_LOSS_UPDATE_SUCCESS: 'stop_loss_update_success',
    STOP_LOSS_UPDATE_FAILED: 'stop_loss_update_failed',
  },
  COPY_TRADE: {
    COPY_TRADE_SUCCESS: 'copy_trade_success',
    COPY_TRADE_FAILED: 'copy_trade_failed',
  },
  ADD_FUNDS: {
    ADD_FUNDS_SUCCESS: 'add_funds_success',
    ADD_FUNDS_FAILED: 'add_funds_failed',
  },
  EXIT_STRATEGY: {
    EXIT_SUCCESS: 'exit_success',
    EXIT_FAILED: 'exit_failed',
  },
  CANCEL_EXIT: {
    CANCEL_EXIT_SUCCESS: 'cancel_exit_success',
    CANCEL_EXIT_FAILED: 'cancel_exit_failed',
  },
  DASHBOARD: {
    DASHBOARD_VIEWED: 'dashboard_viewed',
    TRADE_HISTORY_VIEWED: 'trade_history_viewed',
    SUBSCRIBED_STRATEGIES_VIEWED: 'subscribed_strategies_viewed',
  },
};

const REKYC_VERIFICATION_SOURCES = {
  DEADLINE_STRIP: 'Kyc Refresh Deadline Strip',
  HOME_PAGE: 'Homepage',
  RESTRICTED_INFO_MODAL: 'Rekyc Restrictions Info Modal',
  PROFILE_PAGE: 'Profile Page',
  KYC_RENEW_MODAL: 'Kyc Renew Modal',
  BANK_DETAILS_PAGE: 'Bank Details Page',
  OPEN_ORDERS: 'Open Orders',
  CONDITIONAL_ORDERS: 'Conditional Orders',
  STOP_ORDERS: 'Stop Orders',
  OPEN_POSITIONS: 'Open Positions',
  TRANSACTION_LOGS_ORDER_HISTORY: 'transaction Logs - Order history',
  ASSET_HISTORY: 'Asset History',
  TRANSACTION_LOGS_TRADE_HISTORY: 'transaction Logs - Trade History',
  TRANSFER_HISTORY: 'Transfer History',
  REDUCE_ONLY_STRIP: 'Reduce Only Strip',
  SUB_ACCOUNT_KYC_REFRESH: 'Sub Account Kyc Refresh',
  BANK_DETAILS_REKYC: 'KYC Refresh Bank Details',
  SIGNAL_TRADING_KYC_REFRESH: 'Signal Trading Kyc Refresh',
};

// Sources for events for page or specific feature can be added here. These are passed to mixpanel for tracking
const MIXPANEL_SOURCES = {
  WEB_APP: 'web-app',
  WEB_APP_SIGNUP: 'Web app sign up',
  PROFILE: 'profile',
  SETTINGS_GO_HERE: 'settings_go_here',
  SETTINGS_MANAGE_ALL_PREFERENCES: 'settings_manage_all_preferences',
  FOOTER: 'footer',
  DOT_MENU: '9 dot menu',
  LOGIN_PAGE: 'Login Page',
  MORE_DROPDOWN: 'more_dropdown',
  SECURITY_PAGE: 'Security Page',
  ALL_ALERTS_PAGE: 'all_alerts_page',
  COIN_PAGE: 'coin_page',
  TRADING_APP: 'Trading App',
  MARKETS: 'markets',
  CONTRACT_DROPDOWN: 'contract_dropdown',
};

export { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES, REKYC_VERIFICATION_SOURCES };
